import { useState, useCallback, useEffect } from 'react';
import { Permission } from 'types/permission';
import { usePermissionService } from 'services/permission';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';

interface UsePermissionDataProps {
  autoFetch?: boolean;
}

export function usePermissionData({
  autoFetch = true,
}: UsePermissionDataProps = {}) {
  const permissionService = usePermissionService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(
    async (isReload = false) => {
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      if (isReload) {
        setReloading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const data = await permissionService.getAllPermissions();
        setPermissions(data);
      } catch (err) {
        console.error('Failed to fetch permissions:', err);
        setError('Failed to load permissions. Please try again.');
      } finally {
        setLoading(false);
        setReloading(false);
      }
    },
    [permissionService, isCompanyInitialized, activeCompanyId],
  );

  const reload = useCallback(() => {
    void fetchPermissions(true);
  }, [fetchPermissions]);

  // Initial fetch and fetch on company change
  useEffect(() => {
    if (autoFetch && isCompanyInitialized && activeCompanyId) {
      void fetchPermissions();
    }
  }, [autoFetch, fetchPermissions, isCompanyInitialized, activeCompanyId]);

  const addPermission = useCallback((permission: Permission) => {
    setPermissions((prev) => [permission, ...prev]);
  }, []);

  const updatePermissionState = useCallback((updatedPermission: Permission) => {
    setPermissions((prev) =>
      prev.map((p) => (p.id === updatedPermission.id ? updatedPermission : p)),
    );
  }, []);

  const removePermissionState = useCallback((id: string) => {
    setPermissions((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return {
    permissions,
    loading,
    reloading,
    error,
    reload,
    addPermission,
    updatePermission: updatePermissionState,
    removePermission: removePermissionState,
  };
}
